import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, Eye, ToggleLeft, Loader2, Star, X } from 'lucide-react';
import { adminService } from '../../services/adminService';
import { PageHeader } from '../../components/shared/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';

const emptyProduct = {
  name: '',
  shortDescription: '',
  description: '',
  price: '',
  unit: 'kg',
  isAvailable: true,
  featured: false,
  popupEnabled: false,
  popupOrder: 1,
  categoryId: '',
  categoryName: '',
  primaryImageUrl: '',
  images: []
};

const UNIT_OPTIONS = ['kg', 'kom', 'l', 'g'];

export default function AdminProductsPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [existingImages, setExistingImages] = useState([]);
  const [primaryImageFile, setPrimaryImageFile] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const primaryImageInputRef = useRef(null);
  const additionalImagesInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    Promise.all([
      adminService.getAdminProducts(),
      adminService.getCategories()
    ]).then(([prods, cats]) => {
      setProducts(prods);
      setCategories(cats);
      setError('');
      setLoading(false);
    }).catch((err) => {
      setProducts([]);
      setCategories([]);
      setError(err.message || 'Greška pri učitavanju proizvoda');
      toast.error(err.message || 'Greška pri učitavanju proizvoda');
      setLoading(false);
    });
  }, []);

  const filtered = products.filter(p =>
    !search || p.name.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setEditingProduct(null);
    setForm(emptyProduct);
    setExistingImages([]);
    setPrimaryImageFile(null);
    setImageFiles([]);
    setDialogOpen(true);
  };

  const clearPrimaryImageSelection = () => {
    setPrimaryImageFile(null);
    if (primaryImageInputRef.current) {
      primaryImageInputRef.current.value = '';
    }
  };

  const clearAdditionalImagesSelection = () => {
    setImageFiles([]);
    if (additionalImagesInputRef.current) {
      additionalImagesInputRef.current.value = '';
    }
  };

  const openEdit = async (product) => {
    setEditLoading(true);
    try {
      const [fullProduct, images] = await Promise.all([
        adminService.getAdminProductById(product.id),
        adminService.getProductImages(product.id)
      ]);
      setEditingProduct(product);
      setForm({
        ...emptyProduct,
        ...fullProduct,
        unit: fullProduct.unit || 'kg',
        description: fullProduct.description || '',
        categoryId: fullProduct.categoryId ? String(fullProduct.categoryId) : ''
      });
      setExistingImages(images || []);
      setPrimaryImageFile(null);
      setImageFiles([]);
      setDialogOpen(true);
    } catch (err) {
      toast.error(err?.message || 'Neuspesno učitavanje podataka proizvoda');
    } finally {
      setEditLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (form.popupEnabled) {
        const selectedElsewhere = products.filter(p =>
          p.popupEnabled && (!editingProduct || p.id !== editingProduct.id)
        ).length;
        if (selectedElsewhere >= 3) {
          toast.error('Maksimalno 3 proizvoda mogu biti u popup akciji.');
          setSaving(false);
          return;
        }
      }

      const cat = categories.find(c => String(c.id) === String(form.categoryId));
      const data = {
        ...form,
        categoryId: form.categoryId ? Number(form.categoryId) : null,
        unit: UNIT_OPTIONS.includes(form.unit) ? form.unit : 'kg',
        price: Number(form.price),
        popupOrder: form.popupEnabled ? Math.max(1, Number(form.popupOrder) || 1) : null,
        categoryName: cat?.name || ''
      };
      let targetProductId = null;
      if (editingProduct) {
        const updated = await adminService.updateProduct(editingProduct.id, data);
        setProducts(prev => prev.map(p => p.id === editingProduct.id ? updated : p));
        targetProductId = editingProduct.id;
        toast.success('Proizvod ažuriran');
      } else {
        const newProd = await adminService.createProduct(data);
        setProducts(prev => [...prev, newProd]);
        targetProductId = newProd.id;
        toast.success('Proizvod kreiran');
      }

      if (targetProductId && primaryImageFile) {
        try {
          const uploadedPrimary = await adminService.uploadPrimaryProductImage(targetProductId, primaryImageFile);
          if (uploadedPrimary?.imageUrl) {
            setProducts(prev => prev.map(p => p.id === targetProductId ? { ...p, primaryImageUrl: uploadedPrimary.imageUrl } : p));
          }
          toast.success('Glavna slika je uploadovana');
        } catch (imageError) {
          toast.error(imageError?.message || 'Glavna slika nije uploadovana');
        }
      }

      if (targetProductId && imageFiles.length > 0) {
        try {
          await adminService.uploadProductImages(targetProductId, imageFiles);
          toast.success('Ostale slike su uspesno uploadovane');
        } catch (galleryError) {
          toast.error(galleryError?.message || 'Upload ostalih slika nije uspeo');
        }
      }

      setDialogOpen(false);
      setExistingImages([]);
      clearPrimaryImageSelection();
      clearAdditionalImagesSelection();
    } catch (e) {
      toast.error(e.message || 'Greška pri cuvanju');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveExistingImage = async (image) => {
    if (!editingProduct || !image?.id) return;
    try {
      await adminService.deleteProductImage(image.id);
      let nextPrimary = '';
      setExistingImages(prev => {
        const remaining = prev.filter(item => item.id !== image.id);
        nextPrimary = remaining.find(item => item.imageUrl)?.imageUrl || '';
        return remaining;
      });
      if (form.primaryImageUrl === image.imageUrl) {
        setForm(prev => ({ ...prev, primaryImageUrl: nextPrimary }));
      }
      toast.success('Slika je obrisana');
    } catch (err) {
      toast.error(err?.message || 'Brisanje slike nije uspelo');
    }
  };

  const handleToggleAvailability = async (id) => {
    const updated = await adminService.toggleAvailability(id);
    setProducts(prev => prev.map(p => p.id === id ? updated : p));
    toast.success('Dostupnost promenjena');
  };

  const handleToggleFeatured = async (id) => {
    const updated = await adminService.toggleFeatured(id);
    setProducts(prev => prev.map(p => p.id === id ? updated : p));
    toast.success('Status istaknutog proizvoda promenjen');
  };

  const handleTogglePopup = async (product) => {
    try {
      const updated = await adminService.togglePopup(product.id);
      setProducts(prev => prev.map(p => p.id === product.id ? updated : p));
      toast.success(updated.popupEnabled ? 'Proizvod je dodat u akcijski popup' : 'Proizvod je uklonjen iz akcijskog popup-a');
    } catch (err) {
      toast.error(err?.message || 'Greška pri promeni popup akcije');
    }
  };

  const handleDelete = async (id) => {
    await adminService.deleteProduct(id);
    setProducts(prev => prev.filter(p => p.id !== id));
    toast.success('Proizvod obrisan');
  };

  return (
    <div>
      <PageHeader
        title="Proizvodi"
        subtitle={`${products.length} proizvoda`}
        actions={
          <Button onClick={openCreate} size="sm" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary-light font-ui">
            <Plus size={16} className="mr-1.5" /> Novi proizvod
          </Button>
        }
      />

      {/* Search */}
      <div className="mb-5">
        <Input
          placeholder="Pretrazi proizvode..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full sm:max-w-xs font-ui"
        />
      </div>

      {/* Table */}
      {error && !loading && (
        <div className="mb-4 rounded-lg border border-border bg-card p-3">
          <p className="text-sm text-muted-foreground font-ui">{error}</p>
        </div>
      )}
      <div className="bg-card rounded-xl shadow-card border border-border overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-3">
            {Array(6).fill(0).map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
          </div>
        ) : (
          <>
          <div className="md:hidden p-3 space-y-3">
            {filtered.map(product => (
              <div key={product.id} className="rounded-xl border border-border bg-background p-4 shadow-card">
                <div className="flex items-start gap-3">
                  <img src={product.primaryImageUrl} alt={product.name} className="h-12 w-12 object-cover rounded-lg shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-ui text-sm font-semibold text-foreground truncate">{product.name}</p>
                    <p className="text-xs text-muted-foreground font-ui">{product.categoryName}</p>
                    <p className="mt-1 text-sm font-ui font-semibold text-foreground">{Number(product.price).toLocaleString('sr-RS')} RSD</p>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs font-ui text-muted-foreground">Jedinica: {product.unit}</span>
                  <button
                    onClick={() => handleToggleFeatured(product.id)}
                    className={`transition-colors ${product.featured ? 'text-gold' : 'text-muted-foreground hover:text-gold'}`}
                  >
                    <Star size={18} fill={product.featured ? 'currentColor' : 'none'} />
                  </button>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <Switch
                    checked={Boolean(product.popupEnabled)}
                    onCheckedChange={() => handleTogglePopup(product)}
                  />
                  <span className="text-xs font-ui text-muted-foreground">
                    {product.popupEnabled ? `Akcija ON (${product.popupOrder || 1})` : 'Akcija OFF'}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Switch
                    checked={product.isAvailable}
                    onCheckedChange={() => handleToggleAvailability(product.id)}
                  />
                  <Button variant="ghost" size="sm" onClick={() => openEdit(product)} className="h-8 px-2 font-ui">
                    <Pencil size={14} className="mr-1" /> Izmeni
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 px-2 text-muted-foreground hover:text-destructive">
                        <Trash2 size={14} className="mr-1" /> Obrisi
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle className="font-serif">Obrisati proizvod?</AlertDialogTitle>
                        <AlertDialogDescription className="font-ui">Ova akcija je nepovrativa.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel className="font-ui">Otkazati</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(product.id)} className="bg-destructive text-destructive-foreground font-ui">Obrisi</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/50 border-b border-border">
                  {['Proizvod', 'Kategorija', 'Cena', 'Status', 'Istaknuti proizvodi', 'Akcija', 'Izmeni'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-ui font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map(product => (
                  <tr key={product.id} className="hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img src={product.primaryImageUrl} alt={product.name} className="h-10 w-10 object-cover rounded-lg shrink-0" />
                        <div>
                          <p className="font-ui text-sm font-medium text-foreground">{product.name}</p>
                          <p className="text-xs text-muted-foreground font-ui">{product.unit}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground font-ui">{product.categoryName}</td>
                    <td className="px-4 py-3 font-ui font-semibold text-foreground text-sm">{Number(product.price).toLocaleString('sr-RS')} RSD</td>
                    <td className="px-4 py-3">
                      <Switch
                        checked={product.isAvailable}
                        onCheckedChange={() => handleToggleAvailability(product.id)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleToggleFeatured(product.id)}
                        className={`transition-colors ${
                          product.featured ? 'text-gold' : 'text-muted-foreground hover:text-gold'
                        }`}
                      >
                        <Star size={18} fill={product.featured ? 'currentColor' : 'none'} />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={Boolean(product.popupEnabled)}
                          onCheckedChange={() => handleTogglePopup(product)}
                        />
                        <span className="text-xs font-ui text-muted-foreground">
                          {product.popupEnabled ? `#${product.popupOrder || 1}` : 'OFF'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(product)} className="h-8 w-8 p-0">
                          <Pencil size={14} />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive">
                              <Trash2 size={14} />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle className="font-serif">Obrisati proizvod?</AlertDialogTitle>
                              <AlertDialogDescription className="font-ui">Ova akcija je nepovrativa.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="font-ui">Otkazati</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(product.id)} className="bg-destructive text-destructive-foreground font-ui">Obrisi</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {/* Product dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif">{editingProduct ? 'Izmeni proizvod' : 'Novi proizvod'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="sm:col-span-2">
              <Label className="font-ui text-sm mb-1.5 block">Naziv *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="font-ui" />
            </div>
            <div>
              <Label className="font-ui text-sm mb-1.5 block">Cena (RSD) *</Label>
              <Input type="number" value={form.price} onChange={e => setForm({...form, price: e.target.value})} className="font-ui" />
            </div>
            <div>
              <Label className="font-ui text-sm mb-1.5 block">Jedinica</Label>
              <Select value={form.unit} onValueChange={v => setForm({...form, unit: v})}>
                <SelectTrigger className="font-ui"><SelectValue placeholder="Odaberi jedinicu" /></SelectTrigger>
                <SelectContent>
                  {UNIT_OPTIONS.map(unit => <SelectItem key={unit} value={unit}>{unit}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-ui text-sm mb-1.5 block">Kategorija</Label>
              <Select value={form.categoryId} onValueChange={v => setForm({...form, categoryId: v})}>
                <SelectTrigger className="font-ui"><SelectValue placeholder="Odaberi..." /></SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="font-ui text-sm mb-1.5 block">Kratki opis</Label>
              <Input value={form.shortDescription} onChange={e => setForm({...form, shortDescription: e.target.value})} className="font-ui" />
            </div>
            <div className="sm:col-span-2">
              <Label className="font-ui text-sm mb-1.5 block">Opis</Label>
              <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={4} className="font-ui resize-none" />
            </div>
            <div className="sm:col-span-2">
              <Label className="font-ui text-sm mb-1.5 block">Glavna slika (upload)</Label>
              <Input
                ref={primaryImageInputRef}
                type="file"
                accept="image/*"
                onChange={e => setPrimaryImageFile((e.target.files && e.target.files[0]) || null)}
                className="font-ui"
              />
              {primaryImageFile && (
                <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2">
                  <p className="text-xs text-muted-foreground font-ui truncate">
                    Izabrana glavna slika: {primaryImageFile.name}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={clearPrimaryImageSelection}
                    className="font-ui"
                  >
                    Ukloni
                  </Button>
                </div>
              )}
              {!primaryImageFile && editingProduct && form.primaryImageUrl && (
                <div className="mt-2 flex items-center gap-3 rounded-lg border border-border bg-muted/30 px-3 py-2">
                  <img
                    src={form.primaryImageUrl}
                    alt="Trenutna glavna slika"
                    className="h-12 w-12 rounded-md border border-border object-cover"
                    loading="lazy"
                  />
                  <p className="text-xs text-muted-foreground font-ui">
                    Trenutna glavna slika proizvoda.
                  </p>
                </div>
              )}
              <p className="text-xs text-muted-foreground font-ui mt-1">
                Ova slika se koristi kao glavna slika proizvoda.
              </p>
            </div>
            <div className="sm:col-span-2">
              <Label className="font-ui text-sm mb-1.5 block">Upload slike (vise fajlova)</Label>
              <Input
                ref={additionalImagesInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={e => {
                  const newFiles = Array.from(e.target.files || []);
                  if (newFiles.length === 0) return;
                  setImageFiles(prev => [...prev, ...newFiles]);
                }}
                className="font-ui"
              />
              {editingProduct && existingImages.length > 0 && (
                <div className="mt-2 space-y-2 rounded-lg border border-border bg-muted/30 px-3 py-2">
                  <p className="text-xs text-muted-foreground font-ui">
                    Trenutne dodatne slike: {existingImages.length}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {existingImages.map((image, idx) => (
                      <div key={image.id || `${image.imageUrl}-${idx}`} className="relative">
                        <img
                          src={image.imageUrl}
                          alt={`Postojeca slika ${idx + 1}`}
                          className="h-12 w-12 rounded-md border border-border object-cover"
                          loading="lazy"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveExistingImage(image)}
                          className="absolute -right-2 -top-2 inline-flex h-5 w-5 items-center justify-center rounded-full border border-border bg-card text-muted-foreground hover:text-destructive"
                          aria-label={`Obrisi sliku ${idx + 1}`}
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {imageFiles.length > 0 && (
                <div className="mt-2 space-y-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
                  <p className="text-xs text-muted-foreground font-ui">
                    Izabrano slika: {imageFiles.length}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {imageFiles.map(file => (
                      <Badge key={`${file.name}-${file.size}-${file.lastModified}`} variant="secondary" className="font-ui">
                        {file.name}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={clearAdditionalImagesSelection}
                      className="font-ui"
                    >
                      Ukloni sve
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.isAvailable} onCheckedChange={v => setForm({...form, isAvailable: v})} />
              <Label className="font-ui text-sm">Dostupno</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.featured} onCheckedChange={v => setForm({...form, featured: v})} />
              <Label className="font-ui text-sm">Istaknuti proizvod</Label>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.popupEnabled} onCheckedChange={v => setForm({...form, popupEnabled: v})} />
              <Label className="font-ui text-sm">Prikaži u akcijskom popup-u</Label>
            </div>
            {form.popupEnabled && (
              <div>
                <Label className="font-ui text-sm mb-1.5 block">Akcija redosled u popup-u (1-3)</Label>
                <Input
                  type="number"
                  min="1"
                  max="3"
                  value={form.popupOrder}
                  onChange={e => setForm({...form, popupOrder: Math.min(3, Math.max(1, Number(e.target.value) || 1))})}
                  className="font-ui"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="font-ui">Otkazati</Button>
            <Button
              onClick={handleSave}
              disabled={saving || editLoading || !form.name || !form.price}
              className="bg-primary text-primary-foreground font-ui"
            >
              {(saving || editLoading) && <Loader2 size={16} className="animate-spin mr-2" />}
              Sačuvati
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
